/**
 * Sentry configuration for Global Clean Frontend
 *
 * Initializes error tracking and performance monitoring for the React application.
 * Includes integration with React Router for navigation tracking and session replay
 * for debugging production issues.
 *
 * Story: 1.8 - Error Monitoring & Alerting Setup
 */

import { createElement, type ComponentProps } from "react";
import * as Sentry from "@sentry/react";
import { BrowserRouter } from "react-router-dom";

/** React Router v7 future flags to silence deprecation warnings and opt-in early. */
const ROUTER_FUTURE = {
  v7_startTransition: true,
  v7_relativeSplatPath: true,
} as const;

/**
 * Optional overrides for initSentry (used in tests).
 * When provided, these take precedence over import.meta.env.
 */
export interface InitSentryOverrides {
  dsn?: string;
  environment?: string;
}

/**
 * Initialize Sentry error tracking and performance monitoring.
 *
 * This function should be called BEFORE React renders the application.
 * It sets up automatic error capture, performance tracing, and session replay.
 *
 * Configuration is read from environment variables (or overrides in tests):
 * - VITE_SENTRY_DSN: Sentry project DSN (required)
 * - VITE_SENTRY_ENVIRONMENT: Environment name (development, staging, production)
 *
 * @param overrides - Optional config overrides (for testing). When provided, used instead of import.meta.env.
 *
 * @example
 * ```typescript
 * import { initSentry } from './config/sentry';
 *
 * initSentry();
 * // Now render your React app
 * ```
 */
export function initSentry(overrides?: InitSentryOverrides): void {
  const dsn = overrides?.dsn ?? (import.meta.env.VITE_SENTRY_DSN as string | undefined);
  const environment =
    (overrides?.environment ?? (import.meta.env.VITE_SENTRY_ENVIRONMENT as string)) ||
    "development";

  // Don't initialize if DSN is not configured (expected in dev without .env)
  if (!dsn || dsn === "") {
    if (import.meta.env.DEV) {
      console.debug("Sentry DSN not configured - error tracking disabled");
    }
    return;
  }

  try {
    Sentry.init({
      dsn,
      environment,

      // Performance monitoring
      integrations: [
        // React Router integration for navigation tracking
        Sentry.browserTracingIntegration(),

        // Session replay for debugging production issues
        Sentry.replayIntegration({
          // Mask all text and input content for privacy
          maskAllText: true,
          blockAllMedia: true,
        }),
      ],

      // Sample rate for performance monitoring
      // 0.1 = 10% of transactions, 1.0 = 100% (use 100% in dev, 10% in prod)
      tracesSampleRate: environment === "production" ? 0.1 : 1.0,

      // Session replay sampling
      // Only capture replays for sessions with errors to stay within free tier
      replaysSessionSampleRate: 0.0, // Don't capture normal sessions
      replaysOnErrorSampleRate: 1.0, // Capture 100% of sessions with errors

      // Send default PII (Personally Identifiable Information) - disabled for privacy
      sendDefaultPii: false,

      // Error filtering - ignore non-critical errors
      beforeSend(event, hint) {
        const error = hint.originalException;

        // Ignore network errors (they're often user's connection issues, not our bugs)
        if (error instanceof Error && error.message.includes("Network Error")) {
          return null; // Drop the event
        }

        // Ignore AbortErrors (user canceled requests)
        if (error instanceof Error && error.name === "AbortError") {
          return null;
        }

        return event;
      },

      // Additional configuration
      attachStacktrace: true, // Include stack traces for all events
      maxBreadcrumbs: 50, // Keep last 50 breadcrumbs for debugging
      debug: false, // Enable for local debugging
    });

    console.log(`Sentry initialized successfully (${environment})`);
  } catch (error) {
    console.error("Failed to initialize Sentry:", error);
    // Don't fail the application if Sentry initialization fails
  }
}

/**
 * BrowserRouter with v7 future flags to silence deprecation warnings.
 * Wrapped by Sentry for navigation tracking.
 */
function BrowserRouterWithFuture(props: ComponentProps<typeof BrowserRouter>) {
  return createElement(BrowserRouter, { ...props, future: ROUTER_FUTURE });
}

/**
 * Create a Sentry-wrapped BrowserRouter for automatic navigation tracking.
 *
 * Use this instead of the regular BrowserRouter from react-router-dom
 * to automatically track route changes in Sentry.
 *
 * @example
 * ```typescript
 * import { SentryRouter } from './config/sentry';
 *
 * function App() {
 *   return (
 *     <SentryRouter>
 *       <Routes>
 *         <Route path="/" element={<Home />} />
 *       </Routes>
 *     </SentryRouter>
 *   );
 * }
 * ```
 */
export const SentryRouter = Sentry.withSentryReactRouterV6Routing(BrowserRouterWithFuture);

/**
 * Set user context for Sentry error tracking.
 *
 * Call this function after user logs in to associate errors with the user.
 * This helps identify which users are affected by specific errors.
 *
 * @param userId - User's unique identifier
 * @param email - User's email address
 * @param role - User's role (admin, staff, customer, driver)
 *
 * @example
 * ```typescript
 * import { setUserContext } from './config/sentry';
 *
 * // After successful login
 * setUserContext(user.id, user.email, user.role);
 * ```
 */
export function setUserContext(
  userId: string,
  email: string | null | undefined,
  role?: string
): void {
  try {
    Sentry.setUser({
      id: userId,
      email: email || undefined,
      role,
    });
  } catch (error) {
    // Gracefully handle case where Sentry is not initialized
    console.debug("Failed to set Sentry user context:", error);
  }
}

/**
 * Clear user context from Sentry.
 *
 * Call this function when user logs out to prevent errors from being
 * associated with the previous user.
 *
 * @example
 * ```typescript
 * import { clearUserContext } from './config/sentry';
 *
 * // On logout
 * clearUserContext();
 * ```
 */
export function clearUserContext(): void {
  try {
    Sentry.setUser(null);
  } catch (error) {
    // Gracefully handle case where Sentry is not initialized
    console.debug("Failed to clear Sentry user context:", error);
  }
}

/**
 * Manually capture an exception and send to Sentry.
 *
 * Use this for exceptions that are caught and handled but should still be tracked.
 *
 * @param error - The error to capture
 * @param extraData - Additional context to include with the error
 *
 * @example
 * ```typescript
 * import { captureException } from './config/sentry';
 *
 * try {
 *   riskyOperation();
 * } catch (error) {
 *   captureException(error, { operation: "riskyOperation", userInput: data });
 *   // Handle the error...
 * }
 * ```
 */
export function captureException(error: Error, extraData?: Record<string, any>): void {
  if (extraData) {
    Sentry.withScope((scope) => {
      Object.entries(extraData).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
      Sentry.captureException(error);
    });
  } else {
    Sentry.captureException(error);
  }
}

// Re-export commonly used Sentry functions for convenience
export { ErrorBoundary } from "@sentry/react";
export default Sentry;
